import * as React from 'react'
import {
  CannabinoidMetaDataItem,
  CreateProduct,
  CreateChildProduct,
  ProductMetaDataItem,
} from '../ProductStore'
import { Select, Checkbox } from 'antd-v4'
import { DatePicker, Divider } from 'antd'
import { ThcCbdInputFields } from './ThcCbdInputFields'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { observer, inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { MetaDataModels } from '@getgreenline/products'
import moment from 'moment'
import { CannabinoidTerpeneDrawer } from './CannabinoidTerpeneDrawer'
import { CannabinoidButton } from './CannabinoidButton'
import { TerpeneButton } from './TerpeneButton'
import { BlazeSelectFilter } from '../../../../../components/CustomSelect/BlazeSelectFilter'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props extends InjectedProps {
  index: number
  disabled: boolean
  product: CreateProduct | CreateChildProduct
  cannabinoid: CannabinoidMetaDataItem
  batchNumbers: string[]
  loadBatches: () => void
}

interface State {
  packagedDate?: string | null
  openDrawer: boolean
  drawerTab: string
}

@inject('currentCompanyStore')
@observer
export class MultiBatchCannabinoid extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      openDrawer: false,
      packagedDate: this.props.cannabinoid?.findMetaData(
        MetaDataModels.MetaDataReservedKeys.PACKAGE_DATE,
      )?.metaValue,
      drawerTab: 'cannabinoid',
    }
  }

  closeDrawer = () => {
    this.setState({
      openDrawer: false,
    })
  }

  onClear = () => {
    this.props.cannabinoid.setBatchNumber(undefined)
    this.props.loadBatches && this.props.loadBatches()
    this.props.product.changed = true
  }

  onSelect = (batchNumber: string) => {
    this.props.cannabinoid.setBatchNumber(batchNumber)
    this.props.product.changed = true
  }

  getPackagedDate = () => {
    const packagedDate = this.props.cannabinoid?.findMetaData(
      MetaDataModels.MetaDataReservedKeys.PACKAGE_DATE,
    )?.metaValue

    return packagedDate ? moment(packagedDate) : undefined
  }

  get availableBatchNumbers() {
    const { batchNumbers, product } = this.props
    const { cannabinoidMetaData } = product

    return batchNumbers.filter(
      (batchNumber) => !cannabinoidMetaData.batchMappedCannabinoidMetaData.get(batchNumber),
    )
  }

  render() {
    const { index, disabled, product, cannabinoid, loadBatches } = this.props
    const { cannabinoidMetaData } = product

    return (
      <>
        {index !== 0 && <Divider className='mt-1 mb-3' />}
        {!this.props.currentCompanyStore?.productDetailsTerpenesAndCannabinoids ? (
          <div className='d-flex flex-row flex-wrap'>
            <div className='mr-2 mb-3' style={{ width: 220 }}>
              <label>Lot</label>
              <BlazeSelectFilter<string>
                showSearch={false}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                allowClear
                style={{ width: '100%' }}
                maxTagCount={1}
                maxTagTextLength={13}
                optionLabelProp='label'
                disabled={disabled}
                placeholder='Select lot'
                defaultValue={cannabinoid.batchNumber}
                value={cannabinoid.batchNumber}
                dropdownMatchSelectWidth={false}
                onSelect={this.onSelect}
                onClear={this.onClear}
              >
                {cannabinoid.batchNumber && (
                  <Select.Option
                    key={cannabinoid.batchNumber}
                    value={cannabinoid.batchNumber}
                    label={cannabinoid.batchNumber}
                    className={'mb-3 ant-select-item-option-selected'}
                  >
                    <div className='d-flex justify-content-between mr-2'>
                      <span className='mr-3'>{cannabinoid.batchNumber}</span>
                      <span>{cannabinoid.thcCbdRangeInfo}</span>
                    </div>
                  </Select.Option>
                )}

                {this.availableBatchNumbers.map((batchNumber) => {
                  return (
                    <Select.Option key={batchNumber} value={batchNumber} label={batchNumber}>
                      <div className='d-flex justify-content-between'>
                        <span className='mr-3'>{batchNumber}</span>
                      </div>
                    </Select.Option>
                  )
                })}
              </BlazeSelectFilter>
            </div>

            <div className='mr-2 mb-3'>
              <label>Min/max</label>
              <div>
                <div className='d-flex justify-content-center align-items-center'>
                  <Checkbox
                    checked={cannabinoid.showMaxMin}
                    disabled={disabled}
                    onChange={cannabinoid.toggleShowMaxMin}
                  />
                </div>
              </div>
            </div>

            <div className='d-flex flex-row flex-wrap'>
              <ThcCbdInputFields
                showMaxMin={cannabinoid.showMaxMin}
                disabled={disabled}
                className='mr-2 mb-3'
                onChange={cannabinoid.setValue}
                minTHC={cannabinoid.minTHC}
                thc={cannabinoid.thc}
                maxTHC={cannabinoid.maxTHC}
                minCBD={cannabinoid.minCBD}
                cbd={cannabinoid.cbd}
                maxCBD={cannabinoid.maxCBD}
                product={this.props.product}
              />

              <div className='d-flex align-items-end mb-3'>
                <BlazeButton
                  titleProps={{
                    title: 'Delete',
                  }}
                  buttonProps={{
                    icon: 'delete',
                    type: 'danger',
                    ghost: true,
                    disabled: disabled,
                    onClick: () => {
                      cannabinoidMetaData.removeCannabinoid(index)
                      product.changed = true
                    },
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='d-flex flex-row flex-wrap'>
              <div>
                <label>Lot</label>
                <div className='mr-2 mb-3' style={{ width: 220 }}>
                  <BlazeSelectFilter<string>
                    showSearch={true}
                    getPopupContainer={(triggerNode: HTMLElement) =>
                      triggerNode.parentNode as HTMLElement
                    }
                    allowClear
                    style={{ width: '100%' }}
                    maxTagCount={1}
                    maxTagTextLength={13}
                    optionLabelProp='label'
                    disabled={disabled}
                    placeholder='Select lot'
                    defaultValue={cannabinoid.batchNumber}
                    value={cannabinoid.batchNumber}
                    dropdownMatchSelectWidth={false}
                    onSelect={this.onSelect}
                    onClear={this.onClear}
                  >
                    {cannabinoid.batchNumber && (
                      <Select.Option
                        key={cannabinoid.batchNumber}
                        value={cannabinoid.batchNumber}
                        label={cannabinoid.batchNumber}
                        className={'mb-3 ant-select-item-option-selected'}
                      >
                        <div className='d-flex justify-content-between mr-2'>
                          <span className='mr-3'>{cannabinoid.batchNumber}</span>
                          <span>{cannabinoid.thcCbdRangeInfo}</span>
                        </div>
                      </Select.Option>
                    )}

                    {this.availableBatchNumbers.map((batchNumber) => {
                      return (
                        <Select.Option key={batchNumber} value={batchNumber} label={batchNumber}>
                          <div className='d-flex justify-content-between'>
                            <span className='mr-3'>{batchNumber}</span>
                          </div>
                        </Select.Option>
                      )
                    })}
                  </BlazeSelectFilter>
                </div>
              </div>

              <div className='align-self-end'>
                <label className='d-block'>Packaging Date</label>
                <DatePicker
                  className='mb-2'
                  onChange={(date: any, dateString: string) => {
                    const productMetaDataItem = cannabinoid.findMetaData(
                      MetaDataModels.MetaDataReservedKeys.PACKAGE_DATE,
                    )

                    if (productMetaDataItem) {
                      cannabinoid.removeMetaDataItem(productMetaDataItem)
                    }

                    cannabinoid.addMetaDataItem(
                      new ProductMetaDataItem(
                        MetaDataModels.MetaDataReservedKeys.PACKAGE_DATE,
                        dateString,
                      ),
                    )
                    product.changed = true
                  }}
                  value={this.getPackagedDate()}
                />

                <CannabinoidButton
                  product={product}
                  cannabinoid={cannabinoid}
                  disabled={disabled}
                  onClick={() => {
                    this.setState({ drawerTab: 'cannabinoids', openDrawer: true })
                  }}
                />

                <TerpeneButton
                  cannabinoid={cannabinoid}
                  disabled={disabled}
                  onClick={() => {
                    this.setState({ drawerTab: 'terpenes', openDrawer: true })
                  }}
                />
              </div>

              <div className='d-flex mb-3' style={{ marginLeft: 'auto' }}>
                <BlazeButton
                  titleProps={{
                    title: 'Delete',
                  }}
                  buttonProps={{
                    icon: 'delete',
                    type: 'danger',
                    ghost: true,
                    disabled: disabled,
                    onClick: () => {
                      cannabinoidMetaData.removeCannabinoid(index)
                      product.changed = true
                    },
                  }}
                />
              </div>
            </div>

            {this.state.openDrawer && (
              <CannabinoidTerpeneDrawer
                isOpen={this.state.openDrawer}
                initialTab={this.state.drawerTab}
                onClose={this.closeDrawer}
                index={this.props.index}
                disabled={this.props.disabled}
                product={this.props.product}
                cannabinoid={this.props.cannabinoid}
                availableBatchNumbers={this.availableBatchNumbers}
                loadBatches={loadBatches}
              />
            )}
          </>
        )}
      </>
    )
  }
}
